import { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Upload, Edit } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';

import {
  AdminLogin,
  AdminStats,
  EventsTab,
  CategoriesTab,
  RegistrationsTab,
  ResultsTab,
  GalleriesTab,
  ProductsTab,
  OrdersTab,
  ViewerUsersTab,
} from '@/components/admin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { toast } = useToast();
  const [authenticated, setAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  const [stats, setStats] = useState(null);

  // Data state
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [results, setResults] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [siteSettings, setSiteSettings] = useState({ store_enabled: true });

  // Dialog states
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultUploadDialogOpen, setResultUploadDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [eventTypeDialogOpen, setEventTypeDialogOpen] = useState(false);
  const [editingEventType, setEditingEventType] = useState(null);
  const [productCategoryDialogOpen, setProductCategoryDialogOpen] = useState(false);
  
  const [selectedEvent, setSelectedEvent] = useState('all');
  const fileInputRef = useRef(null);

  // Form states
  const [eventFormData, setEventFormData] = useState({
    name: '', description: '', date: '', location: '', category: '',
    price: 0, max_participants: 100, image_url: '', categories: [], routes: [], price_lots: [], regulamento: '',
    regulamento_mode: 'text', regulamento_file_url: '', regulamento_file_name: '', regulamento_file_type: '',
  });
  const [tempRoutes, setTempRoutes] = useState([]);
  const [tempPriceLots, setTempPriceLots] = useState([]);
  const [tempOptionalItems, setTempOptionalItems] = useState([]);
  const [newOptionalItem, setNewOptionalItem] = useState({ name: '', description: '', price: '', max_quantity: 1 });
  const [newLot, setNewLot] = useState({ name: '', price: '', end_date: '', max_registrations: '' });
  const [resultFormData, setResultFormData] = useState({
    event_id: '', event_name: '', participant_name: '', category: 'Elite', position: 1, time: '',
    percurso: '', equipe: '', cidade: '', estado: '', numero_peito: '',
  });
  const [resultUploadEventId, setResultUploadEventId] = useState('');
  const [galleryFormData, setGalleryFormData] = useState({ event_id: '', title: '', description: '' });
  const [productFormData, setProductFormData] = useState({
    name: '', description: '', price: 0, category: 'Bonés', image_url: '', stock: 0,
  });
  const [registrationFormData, setRegistrationFormData] = useState({});
  const [orderFormData, setOrderFormData] = useState({});
  const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '', age_min: '', age_max: '' });
  const [eventTypeFormData, setEventTypeFormData] = useState({ name: '', description: '', custom_fields: [] });
  const [newCustomField, setNewCustomField] = useState({ name: '', field_key: '', field_type: 'text', placeholder: '', required: false });
  const [productCategoryFormData, setProductCategoryFormData] = useState({ name: '', description: '' });

  useEffect(() => {
    if (authenticated) fetchAllData();
  }, [authenticated]);

  const fetchAllData = async () => {
    try {
      const [eventsRes, resultsRes, productsRes, statsRes, registrationsRes, ordersRes, categoriesRes, routesRes, galleriesRes, eventTypesRes, productCategoriesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/results`),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/registrations`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/routes`),
        axios.get(`${API}/galleries?include_inactive=true`),
        axios.get(`${API}/event-types`),
        axios.get(`${API}/product-categories`),
        axios.get(`${API}/settings`),
      ]);
      setEvents(eventsRes.data);
      setResults(resultsRes.data);
      setProducts(productsRes.data);
      setStats(statsRes.data);
      setRegistrations(registrationsRes.data);
      setOrders(ordersRes.data);
      setCategories(categoriesRes.data);
      setRoutes(routesRes.data);
      setGalleries(galleriesRes.data);
      setEventTypes(eventTypesRes.data);
      setProductCategories(productCategoriesRes.data);
      setSiteSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Event handlers
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, { ...eventFormData, routes: tempRoutes, price_lots: tempPriceLots, optional_items: tempOptionalItems });
      toast({ title: 'Sucesso', description: 'Evento criado com sucesso.' });
      setEventDialogOpen(false);
      resetEventForm();
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível criar o evento.', variant: 'destructive' });
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/events/${editingEvent}`, { ...eventFormData, routes: tempRoutes, price_lots: tempPriceLots, optional_items: tempOptionalItems });
      toast({ title: 'Sucesso', description: 'Evento atualizado com sucesso.' });
      setEventDialogOpen(false);
      setEditingEvent(null);
      resetEventForm();
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível atualizar o evento.', variant: 'destructive' });
    }
  };

  const handleDeleteEvent = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este evento?')) {
      try {
        await axios.delete(`${API}/events/${id}`);
        toast({ title: 'Sucesso', description: 'Evento excluído com sucesso.' });
        fetchAllData();
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível excluir o evento.', variant: 'destructive' });
      }
    }
  };

  const handleToggleEventStatus = async (event) => {
    const newStatus = event.status === 'active' ? 'finished' : 'active';
    const actionText = newStatus === 'finished' ? 'encerrar' : 'reativar';
    
    if (window.confirm(`Tem certeza que deseja ${actionText} o evento "${event.name}"?`)) {
      try {
        // Buscar dados atuais do evento
        const currentEvent = await axios.get(`${API}/events/${event.id}`);
        const eventData = currentEvent.data;
        
        // Atualizar status
        await axios.put(`${API}/events/${event.id}`, {
          ...eventData,
          status: newStatus,
          routes: [] // Routes são gerenciados separadamente
        });
        
        toast({ 
          title: 'Sucesso', 
          description: newStatus === 'finished' 
            ? 'Evento encerrado. Ele não aparecerá mais na lista de inscrições.'
            : 'Evento reativado. Ele voltará a aparecer na lista de inscrições.'
        });
        fetchAllData();
      } catch (error) {
        console.error('Error toggling event status:', error);
        toast({ title: 'Erro', description: 'Não foi possível alterar o status do evento.', variant: 'destructive' });
      }
    }
  };

  const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleRegulamentoFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast({ title: 'Atenção', description: 'Por favor, selecione um arquivo PDF.', variant: 'destructive' });
      return;
    }

    try {
      const dataUrl = await readFileAsDataURL(file);
      setEventFormData({
        ...eventFormData,
        regulamento_mode: 'file',
        regulamento_file_url: dataUrl,
        regulamento_file_name: file.name,
        regulamento_file_type: file.type,
      });
    } catch (error) {
      console.error('Erro ao ler arquivo de regulamento:', error);
      toast({ title: 'Erro', description: 'Não foi possível ler o arquivo de regulamento.', variant: 'destructive' });
    }
  };

  const resetEventForm = () => {
    setEventFormData({
      name: '', description: '', date: '', location: '', category: 'XCO',
      price: 0, max_participants: 100, image_url: '', categories: [], routes: [], price_lots: [], optional_items: [], regulamento: '',
      regulamento_mode: 'text', regulamento_file_url: '', regulamento_file_name: '', regulamento_file_type: '',
    });
    setTempRoutes([]);
    setTempPriceLots([]);
    setTempOptionalItems([]);
    setNewLot({ name: '', price: '', end_date: '', max_registrations: '' });
    setNewOptionalItem({ name: '', description: '', price: '', max_quantity: 1 });
  };

  const openEditEvent = async (event) => {
    setEditingEvent(event.id);
    const routesRes = await axios.get(`${API}/routes?event_id=${event.id}`);
    setTempRoutes(routesRes.data.map(r => ({
      name: r.name, description: r.description, distance_km: r.distance_km, map_url: r.map_url || ''
    })));
    setTempPriceLots(event.price_lots || []);
    setTempOptionalItems(event.optional_items || []);
    setEventFormData({
      name: event.name, description: event.description, date: event.date.split('T')[0],
      location: event.location, category: event.category, price: event.price,
      max_participants: event.max_participants, image_url: event.image_url,
      categories: event.categories || [], routes: [], price_lots: event.price_lots || [],
      optional_items: event.optional_items || [],
      regulamento: event.regulamento || '',
      regulamento_mode: event.regulamento_mode || (event.regulamento_file_url ? 'file' : 'text'),
      regulamento_file_url: event.regulamento_file_url || '',
      regulamento_file_name: event.regulamento_file_name || '',
      regulamento_file_type: event.regulamento_file_type || '',
    });
    setEventDialogOpen(true);
  };

  // Result handlers
  const handleCreateResult = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/results`, resultFormData);
      toast({ title: 'Sucesso', description: 'Resultado adicionado com sucesso.' });
      setResultDialogOpen(false);
      setResultFormData({ event_id: '', event_name: '', participant_name: '', category: 'Elite', position: 1, time: '', percurso: '', equipe: '', cidade: '', estado: '', numero_peito: '' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível adicionar o resultado.', variant: 'destructive' });
    }
  };

  const handleUploadResults = async (e) => {
    const file = e.target.files[0];
    if (!file || !resultUploadEventId) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('event_id', resultUploadEventId);
    
    try {
      const response = await axios.post(`${API}/results/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = response.data;
      toast({ 
        title: 'Upload Concluído', 
        description: `${data.created_count} resultados importados.${data.total_errors > 0 ? ` ${data.total_errors} erros.` : ''}` 
      });
      setResultUploadDialogOpen(false);
      setResultUploadEventId('');
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível importar os resultados.', variant: 'destructive' });
    }
    e.target.value = '';
  };

  const handleDeleteResult = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este resultado?')) {
      try {
        await axios.delete(`${API}/results/${id}`);
        toast({ title: 'Sucesso', description: 'Resultado excluído com sucesso.' });
        fetchAllData();
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível excluir o resultado.', variant: 'destructive' });
      }
    }
  };

  const handleDeleteResultsByEvent = async (eventId) => {
    if (window.confirm('Tem certeza que deseja excluir TODOS os resultados deste evento?')) {
      try {
        await axios.delete(`${API}/results/by-event/${eventId}`);
        toast({ title: 'Sucesso', description: 'Resultados excluídos com sucesso.' });
        fetchAllData();
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível excluir os resultados.', variant: 'destructive' });
      }
    }
  };

  // Gallery handlers
  const handleCreateGallery = async (e) => {
    e.preventDefault();
    try {
      const event = events.find(ev => ev.id === galleryFormData.event_id);
      const data = { ...galleryFormData, title: galleryFormData.title || event?.name || 'Nova Galeria' };
      await axios.post(`${API}/galleries`, data);
      toast({ title: 'Sucesso', description: 'Galeria criada com sucesso.' });
      setGalleryDialogOpen(false);
      setGalleryFormData({ event_id: '', title: '', description: '' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível criar a galeria.', variant: 'destructive' });
    }
  };

  // Product handlers
  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, productFormData);
      toast({ title: 'Sucesso', description: 'Produto criado com sucesso.' });
      setProductDialogOpen(false);
      resetProductForm();
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível criar o produto.', variant: 'destructive' });
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/products/${editingProduct}`, productFormData);
      toast({ title: 'Sucesso', description: 'Produto atualizado com sucesso.' });
      setProductDialogOpen(false);
      setEditingProduct(null);
      resetProductForm();
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível atualizar o produto.', variant: 'destructive' });
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        await axios.delete(`${API}/products/${id}`);
        toast({ title: 'Sucesso', description: 'Produto excluído com sucesso.' });
        fetchAllData();
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível excluir o produto.', variant: 'destructive' });
      }
    }
  };

  const resetProductForm = () => {
    setProductFormData({ name: '', description: '', price: 0, category: 'Bonés', image_url: '', stock: 0 });
  };

  const openEditProduct = (product) => {
    setEditingProduct(product.id);
    setProductFormData({
      name: product.name, description: product.description, price: product.price,
      category: product.category, image_url: product.image_url, stock: product.stock,
    });
    setProductDialogOpen(true);
  };

  // Registration handlers
  const openEditRegistration = (registration) => {
    setEditingRegistration(registration.id);
    setRegistrationFormData({
      nome: registration.nome,
      sobrenome: registration.sobrenome,
      email: registration.email,
      telefone: registration.telefone,
      whatsapp: registration.whatsapp,
      cpf: registration.cpf,
      rg: registration.rg,
      data_nascimento: registration.data_nascimento,
      sexo: registration.sexo,
      profissao: registration.profissao,
      telefone_emergencia: registration.telefone_emergencia,
      contato_emergencia: registration.contato_emergencia,
      rua: registration.rua,
      numero: registration.numero,
      complemento: registration.complemento,
      bairro: registration.bairro,
      cidade: registration.cidade,
      estado: registration.estado,
      cep: registration.cep,
      pais: registration.pais,
      percurso: registration.percurso,
      categoria: registration.categoria,
      tamanho_camisa: registration.tamanho_camisa,
      marca_bike: registration.marca_bike,
      equipe: registration.equipe,
      observacoes: registration.observacoes,
      payment_status: registration.payment_status,
      // Campos do responsável
      nome_responsavel: registration.nome_responsavel,
      cpf_responsavel: registration.cpf_responsavel,
      telefone_responsavel: registration.telefone_responsavel,
      menor_autorizado: registration.menor_autorizado,
    });
    setRegistrationDialogOpen(true);
  };

  const handleUpdateRegistration = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/registrations/${editingRegistration}`, registrationFormData);
      toast({ title: 'Sucesso', description: 'Inscrição atualizada com sucesso.' });
      setRegistrationDialogOpen(false);
      setEditingRegistration(null);
      setRegistrationFormData({});
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível atualizar a inscrição.', variant: 'destructive' });
    }
  };

  const handleUpdatePaymentStatus = async (registrationId, newStatus) => {
    try {
      await axios.put(`${API}/registrations/${registrationId}`, { payment_status: newStatus });
      toast({ title: 'Sucesso', description: 'Status de pagamento atualizado.' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível atualizar o status.', variant: 'destructive' });
    }
  };

  const handleResendEmail = async (registrationId) => {
    try {
      await axios.post(`${API}/registrations/${registrationId}/resend-email`);
      toast({ title: 'Sucesso', description: 'Email reenviado com sucesso.' });
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível reenviar o email.', variant: 'destructive' });
    }
  };

  // Order handlers
  const openEditOrder = (order) => {
    setEditingOrder(order.id);
    setOrderFormData({
      shipping_status: order.shipping_status, tracking_code: order.tracking_code || '',
      payment_status: order.payment_status,
    });
    setOrderDialogOpen(true);
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/orders/${editingOrder}`, orderFormData);
      toast({ title: 'Sucesso', description: 'Pedido atualizado com sucesso.' });
      setOrderDialogOpen(false);
      setEditingOrder(null);
      setOrderFormData({});
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível atualizar o pedido.', variant: 'destructive' });
    }
  };

  // Category handlers
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, {
        ...categoryFormData,
        age_min: categoryFormData.age_min ? parseInt(categoryFormData.age_min) : null,
        age_max: categoryFormData.age_max ? parseInt(categoryFormData.age_max) : null,
      });
      toast({ title: 'Sucesso', description: 'Categoria criada com sucesso.' });
      setCategoryDialogOpen(false);
      setCategoryFormData({ name: '', description: '', age_min: '', age_max: '' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível criar a categoria.', variant: 'destructive' });
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        await axios.delete(`${API}/categories/${id}`);
        toast({ title: 'Sucesso', description: 'Categoria excluída com sucesso.' });
        fetchAllData();
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível excluir a categoria.', variant: 'destructive' });
      }
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const eventParam = selectedEvent !== 'all' ? `?event_id=${selectedEvent}` : '';
    window.open(`${API}/admin/registrations/export${eventParam}`, '_blank');
    toast({ title: 'Exportando CSV', description: 'O download do arquivo CSV será iniciado em breve.' });
  };

  // Event Type handlers
  const handleCreateEventType = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/event-types`, eventTypeFormData);
      toast({ title: 'Sucesso', description: 'Tipo de prova criado com sucesso.' });
      setEventTypeDialogOpen(false);
      setEventTypeFormData({ name: '', description: '', custom_fields: [] });
      setEditingEventType(null);
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível criar o tipo de prova.', variant: 'destructive' });
    }
  };

  const handleUpdateEventType = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/event-types/${editingEventType}`, eventTypeFormData);
      toast({ title: 'Sucesso', description: 'Tipo de prova atualizado com sucesso.' });
      setEventTypeDialogOpen(false);
      setEventTypeFormData({ name: '', description: '', custom_fields: [] });
      setEditingEventType(null);
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível atualizar o tipo de prova.', variant: 'destructive' });
    }
  };

  const handleDeleteEventType = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este tipo de prova?')) {
      try {
        await axios.delete(`${API}/event-types/${id}`);
        toast({ title: 'Sucesso', description: 'Tipo de prova excluído com sucesso.' });
        fetchAllData();
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível excluir o tipo de prova.', variant: 'destructive' });
      }
    }
  };

  const openEditEventType = (eventType) => {
    setEditingEventType(eventType.id);
    setEventTypeFormData({
      name: eventType.name,
      description: eventType.description || '',
      custom_fields: eventType.custom_fields || [],
    });
    setEventTypeDialogOpen(true);
  };

  const addCustomField = () => {
    if (!newCustomField.name || !newCustomField.field_key) return;
    setEventTypeFormData({
      ...eventTypeFormData,
      custom_fields: [...eventTypeFormData.custom_fields, { ...newCustomField }]
    });
    setNewCustomField({ name: '', field_key: '', field_type: 'text', placeholder: '', required: false });
  };

  const removeCustomField = (index) => {
    setEventTypeFormData({
      ...eventTypeFormData,
      custom_fields: eventTypeFormData.custom_fields.filter((_, i) => i !== index)
    });
  };

  // Product Category handlers
  const handleCreateProductCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/product-categories`, productCategoryFormData);
      toast({ title: 'Sucesso', description: 'Categoria de produto criada com sucesso.' });
      setProductCategoryDialogOpen(false);
      setProductCategoryFormData({ name: '', description: '' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erro', description: error.response?.data?.detail || 'Não foi possível criar a categoria.', variant: 'destructive' });
    }
  };

  const handleDeleteProductCategory = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        await axios.delete(`${API}/product-categories/${id}`);
        toast({ title: 'Sucesso', description: 'Categoria excluída com sucesso.' });
        fetchAllData();
      } catch (error) {
        toast({ title: 'Erro', description: error.response?.data?.detail || 'Não foi possível excluir.', variant: 'destructive' });
      }
    }
  };

  // Site Settings handlers
  const handleToggleStore = async () => {
    try {
      const newValue = !siteSettings.store_enabled;
      await axios.put(`${API}/settings`, { store_enabled: newValue });
      setSiteSettings({ ...siteSettings, store_enabled: newValue });
      toast({ 
        title: newValue ? 'Loja Ativada' : 'Loja Desativada', 
        description: newValue ? 'A loja agora está visível no menu.' : 'A loja foi ocultada do menu.' 
      });
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível alterar a configuração.', variant: 'destructive' });
    }
  };

  const handleLoginSuccess = (token) => {
    setAdminToken(token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    setAuthenticated(true);
  };

  if (!authenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="mb-12">
        <h1 className="text-4xl md:text-6xl font-heading font-black tracking-tight uppercase text-primary mb-4">
          Painel Administrativo
        </h1>
        <p className="text-base md:text-lg text-foreground/70">
          Gerencie eventos, resultados, fotos e produtos.
        </p>
      </div>

      <AdminStats stats={stats} />

      <Tabs defaultValue="events" className="space-y-6">
        <TabsList className="grid w-full grid-cols-9 bg-secondary">
          <TabsTrigger data-testid="tab-events" value="events" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Eventos</TabsTrigger>
          <TabsTrigger data-testid="tab-viewers" value="viewers" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Visualizadores</TabsTrigger>
          <TabsTrigger data-testid="tab-event-types" value="event-types" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Tipos</TabsTrigger>
          <TabsTrigger data-testid="tab-categories" value="categories" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Categorias</TabsTrigger>
          <TabsTrigger data-testid="tab-registrations" value="registrations" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Inscrições</TabsTrigger>
          <TabsTrigger data-testid="tab-results" value="results" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Resultados</TabsTrigger>
          <TabsTrigger data-testid="tab-photos" value="photos" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Galerias</TabsTrigger>
          <TabsTrigger data-testid="tab-products" value="products" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Produtos</TabsTrigger>
          <TabsTrigger data-testid="tab-orders" value="orders" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Pedidos</TabsTrigger>
          <TabsTrigger data-testid="tab-settings" value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest font-bold text-xs">Config</TabsTrigger>
        </TabsList>

        <TabsContent value="events">
          <EventsTab
            events={events}
            eventTypes={eventTypes}
            onCreateNew={() => { setEditingEvent(null); resetEventForm(); setEventDialogOpen(true); }}
            onEdit={openEditEvent}
            onDelete={handleDeleteEvent}
            onToggleStatus={handleToggleEventStatus}
          />
        </TabsContent>

        <TabsContent value="viewers">
          <ViewerUsersTab events={events} adminToken={adminToken} />
        </TabsContent>

        <TabsContent value="event-types">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-heading font-bold uppercase text-primary">Tipos de Prova</h2>
              <Button onClick={() => { setEditingEventType(null); setEventTypeFormData({ name: '', description: '', custom_fields: [] }); setEventTypeDialogOpen(true); }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm">
                <Plus className="w-5 h-5 mr-2" /> Novo Tipo
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {eventTypes.map((et) => (
                <div key={et.id} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-lg text-primary">{et.name}</h3>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEditEventType(et)} className="rounded-sm"><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteEventType(et.id)} className="rounded-sm"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  {et.description && <p className="text-sm text-foreground/70 mb-3">{et.description}</p>}
                  {et.custom_fields?.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-bold uppercase text-foreground/50">Campos Personalizados:</p>
                      {et.custom_fields.map((field, idx) => (
                        <div key={idx} className="text-sm flex items-center gap-2">
                          <span className="text-foreground">{field.name}</span>
                          {field.required && <span className="text-xs text-red-500">*obrigatório</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {(!et.custom_fields || et.custom_fields.length === 0) && (
                    <p className="text-xs text-foreground/50 italic">Sem campos personalizados</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="categories">
          <CategoriesTab
            categories={categories}
            onCreateNew={() => setCategoryDialogOpen(true)}
            onDelete={handleDeleteCategory}
          />
        </TabsContent>

        <TabsContent value="registrations">
          <RegistrationsTab
            events={events}
            registrations={registrations}
            selectedEvent={selectedEvent}
            setSelectedEvent={setSelectedEvent}
            onEditRegistration={openEditRegistration}
            onResendEmail={handleResendEmail}
            onUpdatePaymentStatus={handleUpdatePaymentStatus}
            onExportCSV={handleExportCSV}
          />
        </TabsContent>

        <TabsContent value="results">
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <h2 className="text-2xl font-heading font-bold uppercase text-primary">Gerenciar Resultados</h2>
              <div className="flex gap-2">
                <Button onClick={() => setResultDialogOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm">
                  <Plus className="w-5 h-5 mr-2" /> Manual
                </Button>
                <Button onClick={() => setResultUploadDialogOpen(true)} variant="outline" className="uppercase tracking-widest font-bold px-6 py-3 rounded-sm">
                  <Upload className="w-5 h-5 mr-2" /> Importar CSV
                </Button>
              </div>
            </div>
            <ResultsTab
              results={results}
              events={events}
              onCreateNew={() => setResultDialogOpen(true)}
              onDelete={handleDeleteResult}
              onDeleteByEvent={handleDeleteResultsByEvent}
            />
          </div>
        </TabsContent>

        <TabsContent value="photos">
          <GalleriesTab
            events={events}
            galleries={galleries}
            onCreateNew={() => setGalleryDialogOpen(true)}
            onGalleryDeleted={fetchAllData}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        <TabsContent value="products">
          <ProductsTab
            products={products}
            onCreateNew={() => { setEditingProduct(null); resetProductForm(); setProductDialogOpen(true); }}
            onEdit={openEditProduct}
            onDelete={handleDeleteProduct}
          />
        </TabsContent>

        <TabsContent value="orders">
          <OrdersTab orders={orders} onEditOrder={openEditOrder} />
        </TabsContent>

        <TabsContent value="settings">
          <div className="space-y-8">
            <h2 className="text-2xl font-heading font-bold uppercase text-primary">Configurações do Site</h2>
            
            {/* Configuração da Loja */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-bold text-primary mb-4">Loja Online</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-foreground font-medium">Status da Loja</p>
                  <p className="text-sm text-foreground/60">
                    {siteSettings.store_enabled 
                      ? 'A loja está ativa e visível no menu de navegação.' 
                      : 'A loja está desativada e oculta do menu.'}
                  </p>
                </div>
                <Button 
                  data-testid="toggle-store-btn"
                  onClick={handleToggleStore}
                  className={siteSettings.store_enabled 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'}
                >
                  {siteSettings.store_enabled ? 'Desativar Loja' : 'Ativar Loja'}
                </Button>
              </div>
            </div>

            {/* Categorias de Produtos */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-primary">Categorias de Produtos</h3>
                <Button 
                  onClick={() => setProductCategoryDialogOpen(true)} 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-4 py-2 rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-2" /> Nova Categoria
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {productCategories.map((cat) => (
                  <div key={cat.id} className="bg-secondary/30 border border-border rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <p className="font-bold text-foreground">{cat.name}</p>
                      <p className="text-sm text-foreground/60">{cat.description || 'Sem descrição'}</p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      onClick={() => handleDeleteProductCategory(cat.id)}
                      className="rounded-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {productCategories.length === 0 && (
                  <p className="text-foreground/50 col-span-full text-center py-4">Nenhuma categoria cadastrada.</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Event Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent data-testid="event-dialog" className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">
              {editingEvent ? 'Editar Evento' : 'Novo Evento'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Nome *</Label>
                <Input data-testid="event-name" required value={eventFormData.name} onChange={(e) => setEventFormData({ ...eventFormData, name: e.target.value })} className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4" />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Data *</Label>
                <Input data-testid="event-date" type="date" required value={eventFormData.date} onChange={(e) => setEventFormData({ ...eventFormData, date: e.target.value })} className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Descrição *</Label>
              <Textarea data-testid="event-description" required value={eventFormData.description} onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })} className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm px-4 min-h-24" />
            </div>
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Regulamento</Label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="regulamento-mode"
                    checked={eventFormData.regulamento_mode === 'text'}
                    onChange={() => setEventFormData({ ...eventFormData, regulamento_mode: 'text' })}
                    className="form-radio h-4 w-4"
                  />
                  Texto
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="regulamento-mode"
                    checked={eventFormData.regulamento_mode === 'file'}
                    onChange={() => setEventFormData({ ...eventFormData, regulamento_mode: 'file' })}
                    className="form-radio h-4 w-4"
                  />
                  Upload de PDF
                </label>
              </div>

              {eventFormData.regulamento_mode === 'text' ? (
                <Textarea
                  data-testid="event-regulamento"
                  value={eventFormData.regulamento}
                  onChange={(e) => setEventFormData({ ...eventFormData, regulamento: e.target.value })}
                  className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm px-4 min-h-32"
                  placeholder="Digite o regulamento completo do evento. Este texto será exibido aos participantes antes da inscrição."
                />
              ) : (
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleRegulamentoFileChange}
                    className="w-full text-sm text-foreground"
                  />
                  {eventFormData.regulamento_file_name && (
                    <div className="flex items-center justify-between rounded border border-border bg-secondary/20 p-3">
                      <span className="text-sm text-foreground/90">{eventFormData.regulamento_file_name}</span>
                      <button
                        type="button"
                        onClick={() => setEventFormData({
                          ...eventFormData,
                          regulamento_file_url: '',
                          regulamento_file_name: '',
                          regulamento_file_type: '',
                        })}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remover arquivo
                      </button>
                    </div>
                  )}
                  <p className="text-sm text-foreground/60">
                    Selecione um arquivo PDF para ser aberto diretamente no link de regulamento do evento.
                  </p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Localização *</Label>
                <Input data-testid="event-location" required value={eventFormData.location} onChange={(e) => setEventFormData({ ...eventFormData, location: e.target.value })} className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4" />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Tipo de Prova *</Label>
                <select data-testid="event-category" value={eventFormData.category} onChange={(e) => setEventFormData({ ...eventFormData, category: e.target.value })} className="w-full bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4">
                  <option value="">Selecione o tipo</option>
                  {eventTypes.map((et) => (
                    <option key={et.id} value={et.name}>{et.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Preço (R$) *</Label>
                <Input data-testid="event-price" type="number" step="0.01" required value={eventFormData.price} onChange={(e) => setEventFormData({ ...eventFormData, price: parseFloat(e.target.value) })} className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4" />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Máx. Participantes *</Label>
                <Input data-testid="event-max-participants" type="number" required value={eventFormData.max_participants} onChange={(e) => setEventFormData({ ...eventFormData, max_participants: parseInt(e.target.value) })} className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">URL da Imagem *</Label>
              <Input data-testid="event-image-url" type="url" required value={eventFormData.image_url} onChange={(e) => setEventFormData({ ...eventFormData, image_url: e.target.value })} className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4" />
            </div>

            {/* Categories */}
            <div className="border-t pt-4">
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-4 block">Categorias do Evento</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {categories.map((cat) => (
                  <label key={cat.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-secondary">
                    <input type="checkbox" checked={eventFormData.categories.includes(cat.id)}
                      onChange={(e) => {
                        if (e.target.checked) setEventFormData({ ...eventFormData, categories: [...eventFormData.categories, cat.id] });
                        else setEventFormData({ ...eventFormData, categories: eventFormData.categories.filter(id => id !== cat.id) });
                      }} className="w-4 h-4" />
                    <span className="text-sm font-bold">{cat.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Price Lots */}
            <div className="border-t pt-4">
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-4 block">Lotes de Preço</Label>
              {tempPriceLots.map((lot, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2 p-2 bg-secondary/50 rounded">
                  <span className="flex-1 font-bold">{lot.name}</span>
                  <span>R$ {lot.price}</span>
                  <span className="text-sm text-foreground/70">até {lot.end_date}</span>
                  <Button type="button" variant="destructive" size="sm" onClick={() => setTempPriceLots(tempPriceLots.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              <div className="grid grid-cols-5 gap-2 mt-2">
                <Input placeholder="Nome" value={newLot.name} onChange={(e) => setNewLot({ ...newLot, name: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Input type="number" placeholder="Preço" value={newLot.price} onChange={(e) => setNewLot({ ...newLot, price: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Input type="date" placeholder="Data Fim" value={newLot.end_date} onChange={(e) => setNewLot({ ...newLot, end_date: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Input type="number" placeholder="Máx." value={newLot.max_registrations} onChange={(e) => setNewLot({ ...newLot, max_registrations: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Button type="button" onClick={() => {
                  if (newLot.name && newLot.price && newLot.end_date) {
                    setTempPriceLots([...tempPriceLots, { ...newLot, price: parseFloat(newLot.price), max_registrations: newLot.max_registrations ? parseInt(newLot.max_registrations) : null }]);
                    setNewLot({ name: '', price: '', end_date: '', max_registrations: '' });
                  }
                }} className="h-10 rounded-sm"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            {/* Optional Items */}
            <div className="border-t pt-4">
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-4 block">Itens Opcionais (Adicionais)</Label>
              <p className="text-xs text-foreground/60 mb-3">Adicione itens que podem ser adquiridos junto com a inscrição (ex: camiseta, lanterna, medalha)</p>
              {tempOptionalItems.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2 p-2 bg-green-50 border border-green-200 rounded">
                  <span className="flex-1 font-bold text-green-800">{item.name}</span>
                  <span className="text-green-700">R$ {item.price?.toFixed(2)}</span>
                  <span className="text-xs text-green-600">Máx: {item.max_quantity}</span>
                  <Button type="button" variant="destructive" size="sm" onClick={() => setTempOptionalItems(tempOptionalItems.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              <div className="grid grid-cols-5 gap-2 mt-2">
                <Input 
                  placeholder="Nome do item" 
                  value={newOptionalItem.name} 
                  onChange={(e) => setNewOptionalItem({ ...newOptionalItem, name: e.target.value })} 
                  className="bg-white border border-border rounded-sm h-10 px-3 text-sm col-span-2" 
                />
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="Preço" 
                  value={newOptionalItem.price} 
                  onChange={(e) => setNewOptionalItem({ ...newOptionalItem, price: e.target.value })} 
                  className="bg-white border border-border rounded-sm h-10 px-3 text-sm" 
                />
                <Input 
                  type="number" 
                  placeholder="Qtd Máx" 
                  value={newOptionalItem.max_quantity} 
                  onChange={(e) => setNewOptionalItem({ ...newOptionalItem, max_quantity: parseInt(e.target.value) || 1 })} 
                  className="bg-white border border-border rounded-sm h-10 px-3 text-sm" 
                />
                <Button type="button" onClick={() => {
                  if (newOptionalItem.name && newOptionalItem.price) {
                    setTempOptionalItems([...tempOptionalItems, { 
                      ...newOptionalItem, 
                      price: parseFloat(newOptionalItem.price),
                      is_active: true 
                    }]);
                    setNewOptionalItem({ name: '', description: '', price: '', max_quantity: 1 });
                  }
                }} className="h-10 rounded-sm bg-green-600 hover:bg-green-700"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            {/* Routes */}
            <div className="border-t pt-4">
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-4 block">Percursos</Label>
              {tempRoutes.map((route, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2 p-2 bg-secondary/50 rounded">
                  <span className="flex-1 font-bold">{route.name}</span>
                  <span>{route.distance_km} km</span>
                  {route.map_url && <span className="text-xs text-accent">Com Mapa</span>}
                  <Button type="button" variant="destructive" size="sm" onClick={() => setTempRoutes(tempRoutes.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              <div className="grid grid-cols-5 gap-2 mt-2">
                <Input placeholder="Nome" id="route-name" className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Input placeholder="Descrição" id="route-desc" className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Input type="number" step="0.1" placeholder="Km" id="route-km" className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Input placeholder="URL do Mapa" id="route-map" className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Button type="button" onClick={() => {
                  const name = document.getElementById('route-name').value;
                  const desc = document.getElementById('route-desc').value;
                  const km = document.getElementById('route-km').value;
                  const mapUrl = document.getElementById('route-map').value;
                  if (name && km) {
                    setTempRoutes([...tempRoutes, { name, description: desc || name, distance_km: parseFloat(km), map_url: mapUrl || '' }]);
                    document.getElementById('route-name').value = '';
                    document.getElementById('route-desc').value = '';
                    document.getElementById('route-km').value = '';
                    document.getElementById('route-map').value = '';
                  }
                }} className="h-10 rounded-sm"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">
              {editingEvent ? 'Atualizar Evento' : 'Criar Evento'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={resultDialogOpen} onOpenChange={setResultDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">Novo Resultado</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateResult} className="space-y-4">
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Evento *</Label>
              <select required value={resultFormData.event_id} onChange={(e) => {
                const event = events.find(ev => ev.id === e.target.value);
                setResultFormData({ ...resultFormData, event_id: e.target.value, event_name: event?.name || '' });
              }} className="w-full bg-white border-2 border-border rounded-sm h-12 px-4">
                <option value="">Selecione</option>
                {events.map(event => <option key={event.id} value={event.id}>{event.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Participante *</Label><Input required value={resultFormData.participant_name} onChange={(e) => setResultFormData({ ...resultFormData, participant_name: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Nº Peito/Placa *</Label><Input required value={resultFormData.numero_peito} onChange={(e) => setResultFormData({ ...resultFormData, numero_peito: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Posição *</Label><Input type="number" required value={resultFormData.position} onChange={(e) => setResultFormData({ ...resultFormData, position: parseInt(e.target.value) })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Tempo</Label><Input value={resultFormData.time} onChange={(e) => setResultFormData({ ...resultFormData, time: e.target.value })} placeholder="00:00:00" className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Categoria *</Label><Input required value={resultFormData.category} onChange={(e) => setResultFormData({ ...resultFormData, category: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Percurso</Label><Input value={resultFormData.percurso} onChange={(e) => setResultFormData({ ...resultFormData, percurso: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Equipe</Label><Input value={resultFormData.equipe} onChange={(e) => setResultFormData({ ...resultFormData, equipe: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Cidade</Label><Input value={resultFormData.cidade} onChange={(e) => setResultFormData({ ...resultFormData, cidade: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Estado</Label><Input value={resultFormData.estado} onChange={(e) => setResultFormData({ ...resultFormData, estado: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">Adicionar Resultado</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gallery Dialog */}
      <Dialog open={galleryDialogOpen} onOpenChange={setGalleryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">Nova Galeria</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateGallery} className="space-y-4">
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Evento *</Label>
              <select required value={galleryFormData.event_id} onChange={(e) => setGalleryFormData({ ...galleryFormData, event_id: e.target.value })} className="w-full bg-white border-2 border-border rounded-sm h-12 px-4">
                <option value="">Selecione</option>
                {events.map(event => <option key={event.id} value={event.id}>{event.name}</option>)}
              </select>
            </div>
            <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Título</Label><Input value={galleryFormData.title} onChange={(e) => setGalleryFormData({ ...galleryFormData, title: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Descrição</Label><Textarea value={galleryFormData.description} onChange={(e) => setGalleryFormData({ ...galleryFormData, description: e.target.value })} className="bg-white border-2 border-border rounded-sm px-4 min-h-20" /></div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">Criar Galeria</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">{editingProduct ? 'Editar Produto' : 'Novo Produto'}</DialogTitle></DialogHeader>
          <form onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct} className="space-y-4">
            <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Nome *</Label><Input required value={productFormData.name} onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Descrição *</Label><Textarea required value={productFormData.description} onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })} className="bg-white border-2 border-border rounded-sm px-4 min-h-20" /></div>
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Categoria *</Label>
              <select 
                required
                value={productFormData.category} 
                onChange={(e) => setProductFormData({ ...productFormData, category: e.target.value })} 
                className="w-full bg-white border-2 border-border rounded-sm h-12 px-4"
              >
                <option value="">Selecione uma categoria</option>
                {productCategories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Preço *</Label><Input type="number" step="0.01" required value={productFormData.price} onChange={(e) => setProductFormData({ ...productFormData, price: parseFloat(e.target.value) })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
              <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Estoque *</Label><Input type="number" required value={productFormData.stock} onChange={(e) => setProductFormData({ ...productFormData, stock: parseInt(e.target.value) })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            </div>
            <div><Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">URL da Imagem *</Label><Input type="url" required value={productFormData.image_url} onChange={(e) => setProductFormData({ ...productFormData, image_url: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">{editingProduct ? 'Atualizar' : 'Criar'} Produto</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Registration Edit Dialog */}
      <Dialog open={registrationDialogOpen} onOpenChange={setRegistrationDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">Editar Inscrição</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateRegistration} className="space-y-6">
            {/* Dados Pessoais */}
            <div>
              <h3 className="text-sm font-bold uppercase text-primary mb-3 border-b pb-2">Dados Pessoais</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Nome</Label><Input value={registrationFormData.nome || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, nome: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Sobrenome</Label><Input value={registrationFormData.sobrenome || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, sobrenome: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Email</Label><Input value={registrationFormData.email || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, email: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">CPF</Label><Input value={registrationFormData.cpf || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, cpf: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">RG</Label><Input value={registrationFormData.rg || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, rg: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Data Nascimento</Label><Input type="date" value={registrationFormData.data_nascimento || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, data_nascimento: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div>
                  <Label className="text-xs font-bold uppercase text-primary mb-1">Sexo</Label>
                  <select value={registrationFormData.sexo || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, sexo: e.target.value })} className="w-full bg-white border border-border rounded-sm h-10 px-3">
                    <option value="">Selecione</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                </div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Telefone</Label><Input value={registrationFormData.telefone || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, telefone: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">WhatsApp</Label><Input value={registrationFormData.whatsapp || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, whatsapp: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Profissão</Label><Input value={registrationFormData.profissao || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, profissao: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
              </div>
            </div>

            {/* Contato de Emergência */}
            <div>
              <h3 className="text-sm font-bold uppercase text-primary mb-3 border-b pb-2">Contato de Emergência</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Nome do Contato</Label><Input value={registrationFormData.contato_emergencia || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, contato_emergencia: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Telefone Emergência</Label><Input value={registrationFormData.telefone_emergencia || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, telefone_emergencia: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
              </div>
            </div>

            {/* Endereço */}
            <div>
              <h3 className="text-sm font-bold uppercase text-primary mb-3 border-b pb-2">Endereço</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="md:col-span-2"><Label className="text-xs font-bold uppercase text-primary mb-1">Rua</Label><Input value={registrationFormData.rua || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, rua: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Número</Label><Input value={registrationFormData.numero || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, numero: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Complemento</Label><Input value={registrationFormData.complemento || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, complemento: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Bairro</Label><Input value={registrationFormData.bairro || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, bairro: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Cidade</Label><Input value={registrationFormData.cidade || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, cidade: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div>
                  <Label className="text-xs font-bold uppercase text-primary mb-1">Estado</Label>
                  <select value={registrationFormData.estado || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, estado: e.target.value })} className="w-full bg-white border border-border rounded-sm h-10 px-3">
                    <option value="">Selecione</option>
                    {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">CEP</Label><Input value={registrationFormData.cep || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, cep: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">País</Label><Input value={registrationFormData.pais || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, pais: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
              </div>
            </div>

            {/* Dados do Evento */}
            <div>
              <h3 className="text-sm font-bold uppercase text-primary mb-3 border-b pb-2">Dados do Evento</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Percurso</Label><Input value={registrationFormData.percurso || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, percurso: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Categoria</Label><Input value={registrationFormData.categoria || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, categoria: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div>
                  <Label className="text-xs font-bold uppercase text-primary mb-1">Tamanho Camisa</Label>
                  <select value={registrationFormData.tamanho_camisa || 'M'} onChange={(e) => setRegistrationFormData({ ...registrationFormData, tamanho_camisa: e.target.value })} className="w-full bg-white border border-border rounded-sm h-10 px-3">
                    {['PP', 'P', 'M', 'G', 'GG', 'XG', 'XXG'].map(size => <option key={size} value={size}>{size}</option>)}
                  </select>
                </div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Marca Bike</Label><Input value={registrationFormData.marca_bike || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, marca_bike: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div><Label className="text-xs font-bold uppercase text-primary mb-1">Equipe</Label><Input value={registrationFormData.equipe || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, equipe: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
                <div>
                  <Label className="text-xs font-bold uppercase text-primary mb-1">Status Pagamento</Label>
                  <select value={registrationFormData.payment_status || 'pending'} onChange={(e) => setRegistrationFormData({ ...registrationFormData, payment_status: e.target.value })} className="w-full bg-white border border-border rounded-sm h-10 px-3">
                    <option value="pending">Pendente</option>
                    <option value="paid">Pago</option>
                    <option value="failed">Falhou</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-xs font-bold uppercase text-primary mb-1">Observações</Label>
                <Textarea value={registrationFormData.observacoes || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, observacoes: e.target.value })} className="bg-white border border-border rounded-sm px-3 min-h-16" />
              </div>
            </div>

            {/* Dados do Responsável e Autorização do Menor */}
            {(registrationFormData.nome_responsavel || registrationFormData.cpf_responsavel || registrationFormData.telefone_responsavel) && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <h3 className="text-sm font-bold uppercase text-yellow-700 mb-3 border-b border-yellow-200 pb-2">Dados do Responsável (Menor de Idade)</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><Label className="text-xs font-bold uppercase text-yellow-700 mb-1">Nome do Responsável</Label><Input value={registrationFormData.nome_responsavel || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, nome_responsavel: e.target.value })} className="bg-white border border-yellow-300 rounded-sm h-10 px-3" /></div>
                  <div><Label className="text-xs font-bold uppercase text-yellow-700 mb-1">CPF do Responsável</Label><Input value={registrationFormData.cpf_responsavel || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, cpf_responsavel: e.target.value })} className="bg-white border border-yellow-300 rounded-sm h-10 px-3" /></div>
                  <div><Label className="text-xs font-bold uppercase text-yellow-700 mb-1">Telefone do Responsável</Label><Input value={registrationFormData.telefone_responsavel || ''} onChange={(e) => setRegistrationFormData({ ...registrationFormData, telefone_responsavel: e.target.value })} className="bg-white border border-yellow-300 rounded-sm h-10 px-3" /></div>
                </div>
                <div className="mt-4">
                  <label className="flex items-center gap-3 cursor-pointer bg-white p-3 rounded-lg border border-yellow-300">
                    <input 
                      type="checkbox" 
                      data-testid="checkbox-menor-autorizado"
                      checked={registrationFormData.menor_autorizado || false} 
                      onChange={(e) => setRegistrationFormData({ ...registrationFormData, menor_autorizado: e.target.checked })} 
                      className="w-5 h-5" 
                    />
                    <span className="text-sm font-bold text-yellow-700">Participação do menor AUTORIZADA pelo responsável</span>
                  </label>
                </div>
              </div>
            )}

            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">Salvar Alterações</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Order Edit Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">Editar Pedido</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateOrder} className="space-y-4">
            <div>
              <Label className="text-sm font-bold uppercase text-primary mb-1">Status de Envio</Label>
              <select value={orderFormData.shipping_status || 'pending'} onChange={(e) => setOrderFormData({ ...orderFormData, shipping_status: e.target.value })} className="w-full bg-white border border-border rounded-sm h-10 px-3">
                <option value="pending">Pendente</option>
                <option value="sent">Enviado</option>
                <option value="delivered">Entregue</option>
                <option value="pickup_ready">Pronto p/ Retirada</option>
              </select>
            </div>
            <div><Label className="text-sm font-bold uppercase text-primary mb-1">Código de Rastreio</Label><Input value={orderFormData.tracking_code || ''} onChange={(e) => setOrderFormData({ ...orderFormData, tracking_code: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3" /></div>
            <div>
              <Label className="text-sm font-bold uppercase text-primary mb-1">Status de Pagamento</Label>
              <select value={orderFormData.payment_status || 'pending'} onChange={(e) => setOrderFormData({ ...orderFormData, payment_status: e.target.value })} className="w-full bg-white border border-border rounded-sm h-10 px-3">
                <option value="pending">Pendente</option>
                <option value="paid">Pago</option>
                <option value="failed">Falhou</option>
              </select>
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">Salvar Alterações</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">Nova Categoria</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div><Label className="text-sm font-bold uppercase text-primary mb-2">Nome *</Label><Input required value={categoryFormData.name} onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            <div><Label className="text-sm font-bold uppercase text-primary mb-2">Descrição</Label><Textarea value={categoryFormData.description} onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })} className="bg-white border-2 border-border rounded-sm px-4 min-h-20" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-bold uppercase text-primary mb-2">Idade Mín.</Label><Input type="number" value={categoryFormData.age_min} onChange={(e) => setCategoryFormData({ ...categoryFormData, age_min: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
              <div><Label className="text-sm font-bold uppercase text-primary mb-2">Idade Máx.</Label><Input type="number" value={categoryFormData.age_max} onChange={(e) => setCategoryFormData({ ...categoryFormData, age_max: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">Criar Categoria</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Event Type Dialog */}
      <Dialog open={eventTypeDialogOpen} onOpenChange={setEventTypeDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">{editingEventType ? 'Editar Tipo de Prova' : 'Novo Tipo de Prova'}</DialogTitle></DialogHeader>
          <form onSubmit={editingEventType ? handleUpdateEventType : handleCreateEventType} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm font-bold uppercase text-primary mb-2">Nome *</Label><Input required value={eventTypeFormData.name} onChange={(e) => setEventTypeFormData({ ...eventTypeFormData, name: e.target.value })} placeholder="Ex: MTB, Motocross" className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
              <div><Label className="text-sm font-bold uppercase text-primary mb-2">Descrição</Label><Input value={eventTypeFormData.description} onChange={(e) => setEventTypeFormData({ ...eventTypeFormData, description: e.target.value })} placeholder="Descrição do tipo de prova" className="bg-white border-2 border-border rounded-sm h-12 px-4" /></div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-4 block">Campos Personalizados</Label>
              <p className="text-xs text-foreground/60 mb-4">Adicione campos que serão exibidos no formulário de inscrição para este tipo de prova.</p>
              
              {eventTypeFormData.custom_fields?.map((field, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2 p-2 bg-secondary/50 rounded">
                  <span className="flex-1 font-bold">{field.name}</span>
                  <span className="text-sm text-foreground/60">[{field.field_key}]</span>
                  <span className="text-sm">{field.field_type}</span>
                  {field.required && <span className="text-xs text-red-500">Obrigatório</span>}
                  <Button type="button" variant="destructive" size="sm" onClick={() => removeCustomField(idx)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              
              <div className="grid grid-cols-5 gap-2 mt-4">
                <Input placeholder="Nome do Campo" value={newCustomField.name} onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <Input placeholder="Chave (sem espaços)" value={newCustomField.field_key} onChange={(e) => setNewCustomField({ ...newCustomField, field_key: e.target.value.replace(/\s/g, '_').toLowerCase() })} className="bg-white border border-border rounded-sm h-10 px-3 text-sm" />
                <select value={newCustomField.field_type} onChange={(e) => setNewCustomField({ ...newCustomField, field_type: e.target.value })} className="bg-white border border-border rounded-sm h-10 px-2 text-sm">
                  <option value="text">Texto</option>
                  <option value="number">Número</option>
                  <option value="select">Seleção</option>
                </select>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={newCustomField.required} onChange={(e) => setNewCustomField({ ...newCustomField, required: e.target.checked })} />
                  Obrigatório
                </label>
                <Button type="button" onClick={addCustomField} className="h-10 rounded-sm"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">{editingEventType ? 'Atualizar' : 'Criar'} Tipo de Prova</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Result Upload Dialog */}
      <Dialog open={resultUploadDialogOpen} onOpenChange={setResultUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">Importar Resultados (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold uppercase text-primary mb-2">Evento *</Label>
              <select value={resultUploadEventId} onChange={(e) => setResultUploadEventId(e.target.value)} className="w-full bg-white border-2 border-border rounded-sm h-12 px-4">
                <option value="">Selecione o evento</option>
                {events.map(event => <option key={event.id} value={event.id}>{event.name}</option>)}
              </select>
            </div>
            <div className="p-4 bg-secondary/30 rounded-lg text-sm space-y-2">
              <p className="font-bold">Formato do CSV:</p>
              <p className="text-foreground/70">O arquivo deve ter cabeçalho com as colunas:</p>
              <code className="block bg-background p-2 rounded text-xs">posicao;nome;categoria;tempo;percurso;equipe;cidade;estado;numero_peito</code>
              <p className="text-foreground/70">Colunas obrigatórias: <strong>posicao</strong>, <strong>nome</strong> e <strong>numero_peito</strong></p>
            </div>
            <input type="file" accept=".csv" ref={fileInputRef} onChange={handleUploadResults} className="hidden" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={!resultUploadEventId} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">
              <Upload className="w-5 h-5 mr-2" /> Selecionar Arquivo CSV
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Category Dialog */}
      <Dialog open={productCategoryDialogOpen} onOpenChange={setProductCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">Nova Categoria de Produto</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateProductCategory} className="space-y-4">
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Nome *</Label>
              <Input 
                required 
                value={productCategoryFormData.name} 
                onChange={(e) => setProductCategoryFormData({ ...productCategoryFormData, name: e.target.value })} 
                className="bg-white border-2 border-border rounded-sm h-12 px-4"
                placeholder="Ex: Acessórios, Vestuário, Bonés"
              />
            </div>
            <div>
              <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Descrição</Label>
              <Textarea 
                value={productCategoryFormData.description} 
                onChange={(e) => setProductCategoryFormData({ ...productCategoryFormData, description: e.target.value })} 
                className="bg-white border-2 border-border rounded-sm px-4 min-h-20"
                placeholder="Descrição opcional da categoria"
              />
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm">Criar Categoria</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
